/**
 * Écran « Comptes de TVA » : quels comptes de la comptabilité la déclaration de TVA lit pour chaque rôle.
 * Un motif est un compte exact (4431) ou un préfixe suivi d'une étoile (4431*).
 */
import { esc } from '@presentation/shell/format';
import {
  ComptesTvaInvalideError,
  ROLES_COMPTE_TVA,
  type RoleCompteTva,
  type SurchargesComptesTva,
  type TableComptesTva,
} from '@domain/fiscalite/ComptesTva';
import {
  AIDE_MOTIFS_TVA,
  MENTION_COMPTE_DE_LIQUIDATION,
  NOTE_COMPTE_4441,
  construireLignesComptesTva,
  motifsDepuisTexte,
  validerSaisieComptesTva,
} from '@application/comptabilite/EcranComptesTva';
import {
  STYLE_AIDE,
  STYLE_TH_LIGNE,
  bandeauExpertHtml,
  erreurChampHtml,
  majErreurChamp,
  majResume,
  majStatut,
  type Confirmer,
  type Notifier,
} from './panelKit';

export const DESCRIPTION_AUDIT_COMPTES_TVA = 'Table des comptes de TVA';

export interface ComptesTvaDeps {
  canMutate: boolean;
  load(): Promise<TableComptesTva>;
  save(surcharges: SurchargesComptesTva): Promise<TableComptesTva>;
  audit(description: string, payload?: Record<string, unknown>): Promise<void>;
  confirm: Confirmer;
  notify: Notifier;
}

const P = 'cpt-tva';

export async function mountComptesTvaPanel(host: HTMLElement, deps: ComptesTvaDeps): Promise<void> {
  let table = await deps.load();
  /** Motifs en cours d'édition, par rôle (la table enregistrée n'est modifiée qu'à l'enregistrement). */
  let saisie: Record<RoleCompteTva, string[]> = copie(table);

  function copie(t: TableComptesTva): Record<RoleCompteTva, string[]> {
    const out = {} as Record<RoleCompteTva, string[]>;
    for (const r of ROLES_COMPTE_TVA) out[r] = [...t[r]];
    return out;
  }

  const afficherErreurs = (): ReturnType<typeof validerSaisieComptesTva> => {
    const r = validerSaisieComptesTva(saisie);
    const erreurs = r.ok ? {} : r.erreurs;
    for (const role of ROLES_COMPTE_TVA) {
      majErreurChamp(host, host.querySelector<HTMLInputElement>(`#${P}-in-${role}`), `${P}-err-${role}`, erreurs[role]);
    }
    return r;
  };

  const render = (focusRole?: RoleCompteTva): void => {
    const lignes = construireLignesComptesTva(table);
    const corps = lignes
      .map((l) => {
        const idErr = `${P}-err-${l.role}`;
        const idAide = `${P}-aide-${l.role}`;
        const puces = saisie[l.role]
          .map((m) =>
            deps.canMutate
              ? `<li style="display:inline-flex;align-items:center;gap:4px;margin:0 6px 6px 0;"><span class="pill teal mono">${esc(m)}</span>
                   <button type="button" class="btn btn-ghost btn-sm" data-retirer="${l.role}" data-motif="${esc(m)}"
                     aria-label="Retirer le compte ${esc(m)} de « ${esc(l.libelle)} »">×</button></li>`
              : `<li style="display:inline-block;margin:0 6px 6px 0;"><span class="pill teal mono">${esc(m)}</span></li>`,
          )
          .join('');
        const saisieHtml = deps.canMutate
          ? `<div class="field" style="max-width:420px;">
               <label for="${P}-in-${l.role}" style="text-transform:none;font-size:12px;">Ajouter des comptes (séparés par des virgules)</label>
               <div style="display:flex;gap:6px;">
                 <input id="${P}-in-${l.role}" type="text" autocomplete="off" class="mono" style="flex:1;min-width:0;"
                   aria-describedby="${idAide} ${idErr}" placeholder="exemple : 4431, 4457*">
                 <button type="button" class="btn btn-secondary btn-sm" data-ajouter="${l.role}">Ajouter</button>
               </div>
               ${erreurChampHtml(idErr, undefined)}
             </div>`
          : '';
        return `<tr data-role-ligne="${l.role}">
          <th scope="row" style="${STYLE_TH_LIGNE}vertical-align:top;">${esc(l.libelle)}${l.modifie ? ' <span class="pill info">Modifié</span>' : ''}
            ${l.mentionLiquidation ? `<div style="font-weight:400;font-size:12px;color:var(--ardoise);margin-top:4px;max-width:260px;">${esc(MENTION_COMPTE_DE_LIQUIDATION)}</div>` : ''}
          </th>
          <td style="vertical-align:top;">
            <ul aria-label="Comptes lus pour « ${esc(l.libelle)} »" style="list-style:none;margin:0;padding:0;">${puces || '<li style="color:var(--ardoise);font-size:12px;">Aucun compte</li>'}</ul>
            ${saisieHtml}
            <p id="${idAide}" style="${STYLE_AIDE}margin:4px 0 0;">${esc(AIDE_MOTIFS_TVA)}</p>
          </td>
        </tr>`;
      })
      .join('');

    host.innerHTML = `
      <div class="card" id="${P}-panel" aria-labelledby="${P}-titre">
        <h2 class="card-title" id="${P}-titre">Comptes de TVA</h2>
        ${bandeauExpertHtml()}
        <p style="${STYLE_AIDE}">
          La déclaration de TVA lit ces comptes pour calculer la TVA collectée (sur vos ventes) et la TVA déductible (sur vos achats).
          ${deps.canMutate ? '' : '<strong>Lecture seule : seuls l’administrateur, le gestionnaire RH et le comptable peuvent modifier ces comptes.</strong>'}
        </p>
        <div id="${P}-resume" class="form-error-summary" role="alert" hidden></div>
        <table class="data" id="${P}-table">
          <caption class="sr-only">Comptes de TVA lus par la déclaration, un rôle par ligne</caption>
          <thead><tr><th scope="col">Rôle</th><th scope="col">Comptes lus</th></tr></thead>
          <tbody>${corps}</tbody>
        </table>
        <p id="${P}-note-4441" style="${STYLE_AIDE}margin-top:12px;">${esc(NOTE_COMPTE_4441)}</p>
        <p id="${P}-statut" role="status" style="${STYLE_AIDE}"></p>
        ${
          deps.canMutate
            ? `<div style="display:flex;gap:10px;flex-wrap:wrap;margin-top:8px;">
                 <button type="button" class="btn btn-primary" id="${P}-enregistrer">Enregistrer les comptes de TVA</button>
                 <button type="button" class="btn btn-secondary" id="${P}-reinitialiser">Réinitialiser aux valeurs par défaut</button>
               </div>`
            : ''
        }
      </div>`;
    bind();
    afficherErreurs();
    if (focusRole) host.querySelector<HTMLInputElement>(`#${P}-in-${focusRole}`)?.focus();
  };

  const ajouter = (role: RoleCompteTva): void => {
    const input = host.querySelector<HTMLInputElement>(`#${P}-in-${role}`);
    if (!input) return;
    const nouveaux = motifsDepuisTexte(input.value).filter((m) => !saisie[role].includes(m));
    if (nouveaux.length === 0) {
      input.value = '';
      return;
    }
    saisie = { ...saisie, [role]: [...saisie[role], ...nouveaux] };
    majResume(host.querySelector<HTMLElement>(`#${P}-resume`), null);
    render(role);
  };

  const bind = (): void => {
    host.querySelectorAll<HTMLButtonElement>('[data-ajouter]').forEach((btn) => {
      btn.addEventListener('click', () => ajouter(btn.dataset['ajouter'] as RoleCompteTva));
    });
    host.querySelectorAll<HTMLInputElement>(`input[id^="${P}-in-"]`).forEach((input) => {
      input.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        ajouter(input.id.slice(`${P}-in-`.length) as RoleCompteTva);
      });
    });
    host.querySelectorAll<HTMLButtonElement>('[data-retirer]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const role = btn.dataset['retirer'] as RoleCompteTva;
        saisie = { ...saisie, [role]: saisie[role].filter((m) => m !== btn.dataset['motif']) };
        majResume(host.querySelector<HTMLElement>(`#${P}-resume`), null);
        render(role);
      });
    });
    host.querySelector(`#${P}-enregistrer`)?.addEventListener('click', () => void enregistrer());
    host.querySelector(`#${P}-reinitialiser`)?.addEventListener('click', () => void reinitialiser());
  };

  const modifications = (avant: TableComptesTva, apres: TableComptesTva): Record<string, unknown>[] =>
    ROLES_COMPTE_TVA.filter((r) => JSON.stringify(avant[r]) !== JSON.stringify(apres[r])).map((r) => ({
      role: r,
      avant: avant[r],
      apres: apres[r],
    }));

  const enregistrer = async (): Promise<void> => {
    if (!deps.canMutate) return;
    // Un texte laissé dans un champ « Ajouter » est pris en compte avant la validation.
    for (const role of ROLES_COMPTE_TVA) {
      const input = host.querySelector<HTMLInputElement>(`#${P}-in-${role}`);
      if (input && input.value.trim() !== '') {
        const nouveaux = motifsDepuisTexte(input.value).filter((m) => !saisie[role].includes(m));
        saisie = { ...saisie, [role]: [...saisie[role], ...nouveaux] };
      }
    }
    const resume = host.querySelector<HTMLElement>(`#${P}-resume`);
    const r = afficherErreurs();
    if (!r.ok) {
      render();
      majResume(host.querySelector<HTMLElement>(`#${P}-resume`), r.resume);
      const premier = ROLES_COMPTE_TVA.find((role) => r.erreurs[role]);
      if (premier) host.querySelector<HTMLInputElement>(`#${P}-in-${premier}`)?.focus();
      return;
    }
    const avant = table;
    try {
      table = await deps.save(r.table);
    } catch (err) {
      if (err instanceof ComptesTvaInvalideError) {
        majResume(resume, err.message);
        return;
      }
      deps.notify.error(err instanceof Error ? err.message : 'Enregistrement impossible.');
      return;
    }
    saisie = copie(table);
    await deps.audit(DESCRIPTION_AUDIT_COMPTES_TVA, { modifications: modifications(avant, table) });
    render();
    host.querySelector<HTMLElement>(`#${P}-enregistrer`)?.focus();
    majStatut(host.querySelector<HTMLElement>(`#${P}-statut`), 'Comptes de TVA enregistrés.');
    deps.notify.success('Comptes de TVA enregistrés.');
  };

  const reinitialiser = async (): Promise<void> => {
    if (!deps.canMutate) return;
    const ok = await deps.confirm({
      title: 'Réinitialiser les comptes de TVA',
      message:
        'Tous les comptes de TVA reprendront les valeurs par défaut du logiciel. Les écritures déjà enregistrées ne sont pas modifiées. Continuer ?',
      confirmLabel: 'Réinitialiser',
      danger: true,
      host,
    });
    if (!ok) return;
    const avant = table;
    try {
      table = await deps.save({});
    } catch (err) {
      deps.notify.error(err instanceof Error ? err.message : 'Réinitialisation impossible.');
      return;
    }
    saisie = copie(table);
    await deps.audit(DESCRIPTION_AUDIT_COMPTES_TVA, { reinitialisation: true, modifications: modifications(avant, table) });
    render();
    host.querySelector<HTMLElement>(`#${P}-reinitialiser`)?.focus();
    majStatut(host.querySelector<HTMLElement>(`#${P}-statut`), 'Comptes de TVA remis aux valeurs par défaut.');
    deps.notify.success('Comptes de TVA remis aux valeurs par défaut.');
  };

  render();
}
